import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Gift, Plus, Pencil, Trash2, Users, BarChart3, TrendingUp, Store, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const CATEGORIES = ["food", "fitness", "wellness", "services", "retail", "beauty",
                    "auto", "petcare", "home", "entertainment", "other"];
const DISCOUNT_TYPES = ["percent", "flat", "bogo", "freebie"];

export default function PerksAdminPage() {
  const { propertyId, properties } = useProperty();
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pending, setPending] = useState({ partners: [], perks: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const empty = {
    property_id: propertyId || "",
    business_name: "", title: "", description: "",
    category: "food", discount_type: "percent", discount_value: 10,
    promo_code: "", redeem_url: "", redeem_instructions: "", address: "",
    starts_at: "", expires_at: "", max_claims: null, logo_url: "",
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const params = { include_expired: true };
      if (propertyId) params.property_id = propertyId;
      const [{ data }, { data: analyticsData }, { data: pendingData }] = await Promise.all([
        api.get("/perks", { params }),
        api.get("/admin/perks/analytics",
                { params: { days: 90, property_id: propertyId || undefined } }),
        api.get("/perk-partners/pending"),
      ]);
      setItems(data.items || []);
      setAnalytics(analyticsData);
      setPending(pendingData);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm((f) => ({ ...f, property_id: propertyId || f.property_id })); }, [propertyId]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      property_id: p.property_id, business_name: p.business_name,
      title: p.title, description: p.description || "",
      category: p.category, discount_type: p.discount_type,
      discount_value: p.discount_value ?? "",
      promo_code: p.promo_code || "", redeem_url: p.redeem_url || "",
      redeem_instructions: p.redeem_instructions || "",
      address: p.address || "", starts_at: p.starts_at || "",
      expires_at: p.expires_at || "", max_claims: p.max_claims,
      logo_url: p.logo_url || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.business_name || !form.title) return toast.error("Business name + title required");
    setBusy(true);
    try {
      const payload = { ...form,
        discount_value: form.discount_value === "" ? null : Number(form.discount_value),
        max_claims: form.max_claims || null };
      if (editing) {
        await api.patch(`/admin/perks/${editing.id}`, payload);
        toast.success("Perk updated");
      } else {
        await api.post("/admin/perks", payload);
        toast.success("Perk published");
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    setBusy(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Archive "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/perks/${p.id}`);
      toast.success("Archived");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const approvePartner = async (p) => {
    try { await api.post(`/perk-partners/${p.id}/approve`); toast.success(`${p.business_name} approved`); await load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Approve failed"); }
  };
  const rejectPartner = async (p) => {
    if (!window.confirm(`Reject ${p.business_name}?`)) return;
    try { await api.post(`/perk-partners/${p.id}/reject`); toast.success("Rejected"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Reject failed"); }
  };
  const approvePerk = async (p) => {
    try { await api.post(`/perk-partners/perks/${p.id}/approve`); toast.success("Perk approved & live"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Approve failed"); }
  };
  const rejectPerk = async (p) => {
    if (!window.confirm(`Reject "${p.title}"?`)) return;
    try { await api.post(`/perk-partners/perks/${p.id}/reject`); toast.success("Perk rejected"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Reject failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="Perks Marketplace"
        subtitle="Publish exclusive discounts from local businesses. Residents see live perks in their portal and can claim codes with one tap."
      />

      {(pending.partners.length > 0 || pending.perks.length > 0) && (
        <Card className="p-4 border-amber-500/30 bg-amber-500/[0.03] mb-4" data-testid="perks-pending-queue">
          <div className="flex items-center gap-2 mb-3">
            <Store className="h-4 w-4 text-amber-400" />
            <div>
              <div className="text-sm font-semibold">Partner review queue</div>
              <div className="text-xs text-zinc-400">{pending.partners.length} new business{pending.partners.length === 1 ? "" : "es"} · {pending.perks.length} perk{pending.perks.length === 1 ? "" : "s"} awaiting your call</div>
            </div>
          </div>
          {pending.partners.length > 0 && (
            <div className="space-y-1.5 mb-3" data-testid="pending-partners">
              {pending.partners.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] p-2" data-testid={`pending-partner-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 truncate">{p.business_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{p.contact_name} · {p.email} · {p.category}</div>
                    {p.description && <div className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{p.description}</div>}
                  </div>
                  <Button size="sm" onClick={() => approvePartner(p)}
                          className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-7"
                          data-testid={`pending-partner-approve-${p.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => rejectPartner(p)}
                          className="border-white/10 rounded-full h-7"
                          data-testid={`pending-partner-reject-${p.id}`}>
                    <XCircle className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              ))}
            </div>
          )}
          {pending.perks.length > 0 && (
            <div className="space-y-1.5" data-testid="pending-perks">
              {pending.perks.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] p-2" data-testid={`pending-perk-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 truncate">{p.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{p.business_name} · {p.discount_type} {p.discount_value ? `· ${p.discount_value}` : ""} {p.promo_code ? `· ${p.promo_code}` : ""}</div>
                  </div>
                  <Button size="sm" onClick={() => approvePerk(p)}
                          className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-7"
                          data-testid={`pending-perk-approve-${p.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => rejectPerk(p)}
                          className="border-white/10 rounded-full h-7"
                          data-testid={`pending-perk-reject-${p.id}`}>
                    <XCircle className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="perk-add-btn">
          <Plus className="h-4 w-4 mr-1" /> New perk
        </Button>
        <div className="ml-auto text-xs text-zinc-500">{items.length} perk{items.length === 1 ? "" : "s"}</div>
      </div>

      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4" data-testid="perks-analytics">
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
              <BarChart3 className="h-3 w-3" /> Top perks · last 90 days
            </div>
            {(analytics.top_perks || []).slice(0, 5).length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center" data-testid="perks-top-chart-empty">No claims yet — publish perks and watch this fill up.</div>
            ) : (
              <div style={{ width: "100%", height: 180 }} data-testid="perks-top-chart">
                <ResponsiveContainer minHeight={180}>
                  <BarChart data={(analytics.top_perks || []).slice(0, 5).map((p) => ({
                    name: (p.title || "").slice(0, 18),
                    claims: p.claim_count_window,
                    business: p.business_name,
                  }))} margin={{ top: 6, right: 6, bottom: 4, left: -14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                    <Bar dataKey="claims" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
              <TrendingUp className="h-3 w-3" /> Claim trend · last 90 days
            </div>
            <div style={{ width: "100%", height: 180 }} data-testid="perks-trend-chart">
              <ResponsiveContainer minHeight={180}>
                <LineChart data={(analytics.daily_trend || []).map((d) => ({
                  date: d.date.slice(5), claims: d.count,
                }))} margin={{ top: 6, right: 6, bottom: 4, left: -14 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 9 }} interval={Math.max(1, Math.floor((analytics.daily_trend?.length || 30) / 8))} />
                  <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                  <Line type="monotone" dataKey="claims" stroke="#818cf8" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
              <Gift className="h-3 w-3" /> Category mix
            </div>
            {(analytics.by_category || []).length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center" data-testid="perks-category-mix-empty">No claims in the window yet.</div>
            ) : (
              <div className="space-y-1.5" data-testid="perks-category-mix">
                {(analytics.by_category || []).slice(0, 6).map((c) => {
                  const max = analytics.by_category[0]?.count || 1;
                  return (
                    <div key={c.category} className="text-xs">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-zinc-400 capitalize">{c.category}</span>
                        <span className="text-zinc-100 tabular-nums">{c.count}</span>
                      </div>
                      <div className="h-1 rounded-full bg-white/[0.04] overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${(c.count / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="text-[11px] text-zinc-500 mt-3 flex items-center justify-between">
              <span>Total claims (90d)</span>
              <span className="text-white tabular-nums text-lg">{analytics.total_claims_window || 0}</span>
            </div>
          </Card>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="p-8 text-center border-white/10" data-testid="perks-admin-empty">
          <Gift className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-zinc-400">No perks published yet. Line up a local business partner and add their offer.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((p) => (
            <Card key={p.id} className="p-4 border-white/10 bg-card" data-testid={`perk-admin-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-100 truncate">{p.title}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{p.business_name} · {p.category}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="outline"
                         className={p.is_active
                           ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                           : "border-white/10 text-zinc-500"}>
                    {p.is_active ? "Live" : "Inactive"}
                  </Badge>
                </div>
              </div>
              {p.description && <div className="text-xs text-zinc-400 line-clamp-2 mb-2">{p.description}</div>}
              <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {p.claim_count || 0} claims</span>
                {p.promo_code && <span className="font-mono text-orange-300">{p.promo_code}</span>}
                {p.expires_at && <span className="ml-auto">Expires {new Date(p.expires_at).toLocaleDateString()}</span>}
              </div>
              <div className="flex items-center gap-1 mt-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="border-white/10 rounded-full"
                        data-testid={`perk-admin-edit-${p.id}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <button onClick={() => remove(p)} className="p-2 text-zinc-500 hover:text-red-300"
                        data-testid={`perk-admin-delete-${p.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[680px] max-h-[90vh] overflow-y-auto" data-testid="perk-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit perk" : "New perk"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Publish an exclusive discount for residents in this community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Community</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="perk-property">
                    <SelectValue placeholder="Choose a community" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="perk-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Business name</Label>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                       placeholder="Sunrise Bakery" data-testid="perk-business"
                       className="bg-white/[0.04] border-white/10" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       placeholder="20% off morning pastries" data-testid="perk-title"
                       className="bg-white/[0.04] border-white/10" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Delicious breakfast pastries — perfect for early risers"
                        className="bg-white/[0.04] border-white/10 text-sm" data-testid="perk-desc" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Discount type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="perk-discount-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {DISCOUNT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Value</Label>
                <Input type="number" value={form.discount_value ?? ""}
                       onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                       placeholder="20" data-testid="perk-discount-value"
                       className="bg-white/[0.04] border-white/10" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Promo code</Label>
                <Input value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                       placeholder="PRESTIGE20" data-testid="perk-promo"
                       className="bg-white/[0.04] border-white/10 font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Redeem URL</Label>
                <Input value={form.redeem_url} onChange={(e) => setForm({ ...form, redeem_url: e.target.value })}
                       placeholder="https://…" data-testid="perk-url"
                       className="bg-white/[0.04] border-white/10" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                       placeholder="123 Main St" data-testid="perk-address"
                       className="bg-white/[0.04] border-white/10" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Redemption instructions</Label>
              <Textarea rows={2} value={form.redeem_instructions}
                        onChange={(e) => setForm({ ...form, redeem_instructions: e.target.value })}
                        placeholder="Show this code at checkout or paste at online cart"
                        className="bg-white/[0.04] border-white/10 text-sm" data-testid="perk-instructions" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Starts (ISO)</Label>
                <Input type="datetime-local" value={form.starts_at?.slice(0, 16) || ""}
                       onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                       className="bg-white/[0.04] border-white/10" data-testid="perk-starts" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Expires (ISO)</Label>
                <Input type="datetime-local" value={form.expires_at?.slice(0, 16) || ""}
                       onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                       className="bg-white/[0.04] border-white/10" data-testid="perk-expires" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Max claims</Label>
                <Input type="number" value={form.max_claims ?? ""}
                       onChange={(e) => setForm({ ...form, max_claims: e.target.value ? Number(e.target.value) : null })}
                       placeholder="unlimited" data-testid="perk-max"
                       className="bg-white/[0.04] border-white/10" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={save} disabled={busy || !form.business_name || !form.title || !form.property_id}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="perk-save-btn">
              {busy ? "Publishing…" : (editing ? "Save changes" : "Publish perk")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
